import { computed, type Ref } from 'vue'

import {
  arrow,
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  useFloating
} from '@floating-ui/vue'

/**
 * Shared Floating UI wiring for anchored surfaces — `AppTooltip`'s bubble and `AppPopover`'s card.
 *
 * Both surfaces are teleported to `body` and positioned with the fixed strategy, so an
 * `overflow: hidden` ancestor can't clip them; triggers sit inside modals, input groups and a
 * sticky header.
 *
 * Pass `beak` to have the arrow middleware track the trigger. `AppTooltip` leaves it out because
 * `CadyTooltip`'s beak sits at one of the board's fixed positions rather than following `shift()`.
 */
export function useFloatingAnchor(
  anchor: Ref<HTMLElement | null>,
  floating: Ref<HTMLElement | null>,
  placement: Ref<Placement>,
  offsetDistance: Ref<number>,
  beak?: Ref<HTMLElement | null>
) {
  return useFloating(anchor, floating, {
    placement,
    strategy: 'fixed',
    // Without this the position is computed once and then goes stale on the first scroll or resize.
    whileElementsMounted: autoUpdate,
    middleware: computed(() => [
      offset(offsetDistance.value),
      flip(),
      shift({ padding: 8 }),
      // Last, so it measures the position the surface actually settled on.
      ...(beak ? [arrow({ element: beak, padding: 8 })] : [])
    ])
  })
}
